<template>
  <div class="driver-page-wrapper p-3">
    <h3 class="page-title mb-3">
      <i class="fa fa-check-square mr-2"></i> Checklist Đầu Ngày
    </h3>
    <p v-if="xeHienTai">Xe: <strong>{{ xeHienTai.bienSo }}</strong> ({{ xeHienTai.tenLoaiXe }})</p>

    <div v-if="isLoading && items.length === 0" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 mb-0 text-muted">Đang tải danh sách hạng mục...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
      <button class="btn btn-sm btn-danger ml-2" @click="loadChecklist">Thử lại</button>
    </div>

    <div v-else-if="items.length === 0" class="text-center py-5 text-muted">
      Không có hạng mục checklist nào được cấu hình cho loại xe này.
    </div>

    <div v-else>
      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <p>Số ODO theo hệ thống: {{ xeHienTai.odoGanNhat }}</p>
          <label>Số ODO hiện tại (Km) <span class="text-danger">*</span></label>
          <input
            v-model.number="odo"
            type="number"
            class="form-control"
            placeholder="Nhập số ODO (bắt buộc)"
            min="0"
          >
        </div>

        <hr>

        <div class="checklist-items-list">
          <div v-for="(item, index) in items" :key="item.id" class="card mb-3 checklist-item">
            <div class="card-body">
              <h5 class="card-title">{{ index + 1 }}. {{ item.tenHangMuc }}</h5>
              <p v-if="item.moTa" class="card-text text-muted small">{{ item.moTa }}</p>

              <div class="btn-group btn-group-toggle d-flex" data-toggle="buttons">
                <label class="btn btn-outline-success w-100" :class="{ active: item.trangThai === 'OK' }">
                  <input type="radio" :name="'item-' + item.id" @change="setItemStatus(item, 'OK')" required>
                  <i class="fa fa-check"></i> OK
                </label>
                <label class="btn btn-outline-danger w-100" :class="{ active: item.trangThai === 'NG' }">
                  <input type="radio" :name="'item-' + item.id" @change="setItemStatus(item, 'NG')">
                  <i class="fa fa-times"></i> LỖI
                </label>
              </div>

              <div v-if="item.trangThai === 'NG'" class="mt-3 p-3 bg-light rounded border animate-fade-in">
                
                <div class="form-group">
                  <label :for="'ghiChu-' + item.id">
                    Ghi chú <span v-if="isRequired(item)" class="text-danger">*</span>
                  </label>
                  <textarea
                    :id="'ghiChu-' + item.id"
                    v-model.trim="item.ghiChu"
                    class="form-control"
                    rows="2"
                    :required="isRequired(item)"
                    placeholder="Mô tả chi tiết lỗi..."
                  ></textarea>
                </div>

                <div class="form-group mb-0">
                  <label>
                    Hình ảnh minh chứng 
                    <span v-if="isRequired(item)" class="text-danger">*</span>
                    <small v-if="item.anhUrls && item.anhUrls.length > 0" class="text-muted">
                        ({{ item.anhUrls.length }} ảnh)
                    </small>
                  </label>

                  <div class="d-flex flex-wrap gap-2 mb-2" v-if="item.anhUrls && item.anhUrls.length > 0">
                    <div 
                        v-for="(url, idx) in item.anhUrls" 
                        :key="idx" 
                        class="position-relative img-thumbnail-wrapper"
                    >
                        <img :src="url" class="img-thumbnail-item" @click="viewImage(url)" />
                        <button 
                            type="button" 
                            class="btn-remove-img" 
                            @click="removeImage(item, idx)"
                        >
                            &times;
                        </button>
                    </div>
                  </div>

                 <button
  type="button"
  class="btn btn-outline-danger btn-block btn-dashed"
  :disabled="isLoading || item.isUploading || cameraUploading"
  @click="openCameraForItem(item)"
>
  <span
    v-if="item.isUploading || cameraUploading"
    class="spinner-border spinner-border-sm mr-1"
  ></span>
  <i v-else class="fa fa-camera mr-1"></i>
  {{
    item.anhUrls && item.anhUrls.length > 0
      ? 'Chụp thêm ảnh'
      : 'Mở camera & chụp'
  }}
</button>

<div
  v-if="item.isUploading"
  class="text-primary small mt-1 text-center"
>
  <span class="spinner-border spinner-border-sm"></span>
  Đang tải ảnh lên...
</div>

                </div>
              </div>

            </div>
          </div>
        </div>

        <hr>
        <div v-if="submitError" class="alert alert-danger">{{ submitError }}</div>
        
        <button
          type="submit"
          class="btn btn-primary btn-lg btn-block shadow-sm"
          :disabled="isLoading || isUploadingAny"
        >
          <span v-if="isLoading || isUploadingAny" class="spinner-border spinner-border-sm"></span>
          {{ isLoading ? 'Đang gửi...' : (isUploadingAny ? 'Đang tải ảnh...' : 'Hoàn tất & Gửi Báo cáo') }}
        </button>
      </form>
    </div>

  </div>

  <FullscreenCamera
  v-model="cameraOpen"
  title="Chụp ảnh lỗi Checklist"
  :busy="cameraUploading"
  initial-facing="environment"
  @confirm="onCameraConfirm"
  @cancel="onCameraCancel"
/>

</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useDriverChecklistStore } from '@/stores/driverChecklistStore';
import { useHoSoXeStore } from '@/stores/hoSoXeStore';
import CameraCapture from '@/components/CameraCapture.vue';
import FullscreenCamera from '@/components/FullscreenCamera.vue';
import fileService from '@/services/fileService';

export default {
  name: 'DriverChecklist',
  components: {
    CameraCapture,
    FullscreenCamera
  },
  data() {
    return {
      odo: null,
      submitError: null,
        cameraOpen: false,
    cameraUploading: false,
    cameraTargetItem: null,
    };
  },
  computed: {
    ...mapState(useDriverChecklistStore, ['isLoading', 'error', 'items']),
    ...mapState(useHoSoXeStore, { xeHienTai: state => state.hoSo.xeHienTai }),
    
    // Check xem có item nào đang upload dở không
    isUploadingAny() {
      return this.items.some(item => item.isUploading);
    }
  },
  methods: {
    ...mapActions(useDriverChecklistStore, ['fetchItems', 'submitChecklist']),
    ...mapActions(useHoSoXeStore, ['fetchHoSoCuaToi']),

     // Mở camera cho 1 item
  openCameraForItem(item) {
    if (!item) return;
    this.cameraTargetItem = item;
    item.isUploading = false;
    this.cameraOpen = true;
  },

  // Khi user chụp xong & bấm "Xác nhận" trong FullscreenCamera
  async onCameraConfirm({ blob }) {
    if (!blob || !this.cameraTargetItem) {
      this.cameraOpen = false;
      return;
    }

    const item = this.cameraTargetItem;
    this.cameraUploading = true;
    item.isUploading = true;
    this.submitError = null;

    try {
      const file = new File(
        [blob],
        `check_${this.xeHienTai?.bienSo || 'xe'}_${item.id}_${Date.now()}.jpg`,
        { type: 'image/jpeg' }
      );

      const formData = new FormData();
      formData.append('files', file, file.name);
      formData.append('folderName', 'checklist');

      const res = await fileService.upload(formData);
      const first = Array.isArray(res.data) ? res.data[0] : null;

      if (!first || !first.location) {
        throw new Error('Server không trả về URL ảnh.');
      }

      if (!item.anhUrls) item.anhUrls = [];
      item.anhUrls.push(first.location);
    } catch (err) {
      console.error('Upload ảnh checklist lỗi:', err);
      this.submitError =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Không upload được ảnh, vui lòng thử lại.';
    } finally {
      this.cameraUploading = false;
      if (this.cameraTargetItem) {
        this.cameraTargetItem.isUploading = false;
      }
      this.cameraTargetItem = null;
      this.cameraOpen = false;
    }
  },

  onCameraCancel() {
    this.cameraOpen = false;
    if (this.cameraTargetItem) {
      this.cameraTargetItem.isUploading = false;
    }
    this.cameraTargetItem = null;
  },

    async loadChecklist() {
      if (!this.xeHienTai) {
        await this.fetchHoSoCuaToi();
      }
      if (this.xeHienTai) {
        await this.fetchItems(this.xeHienTai.id);
        
        // [QUAN TRỌNG] Init mảng anhUrls cho mỗi item sau khi fetch
        this.items.forEach(item => {
            // Vue 2/3 reactivity safety
            if (!item.anhUrls) item.anhUrls = []; 
            if (!item.ghiChu) item.ghiChu = '';
            item.isUploading = false;
        });
      } else {
        this.submitError = 'Không tìm thấy xe được phân công để tải checklist.';
      }
    },

    setItemStatus(item, status) {
      item.trangThai = status;
      if (status === 'OK') {
        item.ghiChu = '';
        item.anhUrls = []; // Reset list ảnh
      } else {
        // Đảm bảo mảng tồn tại khi chuyển sang NG
        if (!item.anhUrls) item.anhUrls = [];
      }
    },

    // [UPDATE] Xử lý khi CameraCapture trả về URL
    handleImageUploaded(item, url) {
        if (url) {
            if (!item.anhUrls) item.anhUrls = [];
            item.anhUrls.push(url);
            item.isUploading = false;
        }
    },

    // [UPDATE] Xóa ảnh khỏi list
    removeImage(item, index) {
        if (item.anhUrls) {
            item.anhUrls.splice(index, 1);
        }
    },

    viewImage(url) {
        window.open(url, '_blank');
    },

    isRequired(item) {
      return item.mucDo === 'CRITICAL' || item.mucDo === 'WARNING';
    },

    // ==== Submit ====
    async handleSubmit() {
      this.submitError = null;

      const uncheckItem = this.items.find(item => item.trangThai === null || item.trangThai === undefined);
      if (uncheckItem) {
        this.submitError = `Bạn chưa kiểm tra hạng mục: "${uncheckItem.tenHangMuc}"`;
        return;
      }

      for (const item of this.items) {
        if (item.trangThai === 'NG' && this.isRequired(item)) {
          // Validate: Phải có ít nhất 1 ảnh
          if (!item.ghiChu || !item.anhUrls || item.anhUrls.length === 0) {
            this.submitError = `Hạng mục "${item.tenHangMuc}" là lỗi quan trọng, yêu cầu Ghi chú VÀ Hình ảnh.`;
            return;
          }
        }
        if (item.trangThai === 'NG' && !this.isRequired(item) && !item.ghiChu) {
          this.submitError = `Vui lòng nhập ghi chú cho hạng mục lỗi "${item.tenHangMuc}".`;
          return;
        }
      }

      const payload = {
        xeId: this.xeHienTai?.id,
        odo: this.odo,
        items: this.items.map(it => ({
          itemId: it.id,
          trangThai: it.trangThai,
          ghiChu: it.ghiChu || null,
          // [UPDATE] Gửi mảng ảnh lên BE
          anhUrls: it.anhUrls || [], 
        })),
      };

      if (!payload.xeId) {
        this.submitError = 'Không xác định được xe để gửi báo cáo.';
        return;
      }

      try {
          const response = await this.submitChecklist(payload);
          
          alert('Gửi báo cáo checklist thành công!');
          
          // Emit event báo hiệu cho component cha (Dashboard)
          if (response.hasCriticalError) {
              this.$emit('checklist-critical-error');
          } else {
              this.$emit('checklist-success');
          }

      } catch (e) {
          this.submitError = this.error || 'Lỗi không xác định khi gửi báo cáo.';
      }
    },
  },
  mounted() {
    this.loadChecklist();
  }
};
</script>

<style scoped>
.checklist-item .card-body { padding: 1rem; }
.checklist-item .btn-group-toggle .btn { font-size: 1.1rem; font-weight: 600; }

/* Active styles */
.btn-outline-success.active { background-color: #198754 !important; color: #fff !important; }
.btn-outline-danger.active { background-color: #dc3545 !important; color: #fff !important; }

/* Style cho Grid ảnh */
.img-thumbnail-wrapper {
    width: 80px;
    height: 80px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    overflow: hidden;
}
.img-thumbnail-item {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
}
.btn-remove-img {
    position: absolute;
    top: 0;
    right: 0;
    width: 24px;
    height: 24px;
    background: rgba(220, 53, 69, 0.9);
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
    border-bottom-left-radius: 6px;
}

.btn-dashed {
    border: 2px dashed #dc3545;
    background: #fff;
    color: #dc3545;
}
.btn-dashed:hover {
    background: #fdf2f2;
}

.animate-fade-in {
    animation: fadeIn 0.3s ease-in;
}
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
}

.gap-2 { gap: 0.5rem; }
</style>